import React, { useState, useEffect, useRef } from 'react';
import { Check, X, ArrowRight, ArrowLeft, ArrowUp, ZoomIn, ZoomOut, RefreshCw } from 'lucide-react';

const QuickDrawCard = ({
  cellData,
  snippet,
  onApprove,
  onSkip,
  onPrevious, 
  canGoBack,  
  isDarkMode,
  cellContent,
  onContentChange,
  imageUrl
}) => {
  const [content, setContent] = useState(cellContent || '');
  const [isEdited, setIsEdited] = useState(false);
  const [isAnimating, setIsAnimating] = useState('');
  const [imageError, setImageError] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Reset state when new cell is loaded
  useEffect(() => {
    setContent(cellContent || '');
    setIsEdited(false);
    setIsAnimating('');
    setImageError(false);
  }, [cellContent, cellData]);

  // Preload the image to get its dimensions
  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
      };
      img.src = imageUrl;
    }
  }, [imageUrl]);

  const handleInputChange = (e) => {
    setContent(e.target.value);
    setIsEdited(true);
    onContentChange(e.target.value);
  };

  const handleApprove = () => {
    // setIsAnimating('right');
    setTimeout(() => {
      onApprove(content);
    }, 100);
  };

  const handleSkip = () => {
    // setIsAnimating('left');
    setTimeout(() => {
      onSkip();
    }, 100);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApprove();
    } else if (e.key === 'Escape') {
      handleSkip();
    } else if (e.key === 'ArrowRight') {
      handleApprove();
    } else if (e.key === 'ArrowLeft') {
      handleSkip();
    } else if (e.key === 'ArrowUp' && canGoBack) {
      onPrevious(); // Use the passed prop
    }
  };

  const hasBoundingBox = () => {
    const bb = cellData?.boundingBox;
    return bb && 
           typeof bb.Left === 'number' && 
           typeof bb.Top === 'number' && 
           typeof bb.Width === 'number' && 
           typeof bb.Height === 'number';
  };

  // Function to directly position an element onto a specific cell in the image
  const renderDirectPositionedImage = () => {
    if (!imageUrl || !hasBoundingBox() || !imageDimensions.width) {
      return null;
    }
  
    const { boundingBox: bb } = cellData;
    const { width: imgWidth, height: imgHeight } = imageDimensions;
  
    // Convert percentages to actual pixel values
    const cellLeft = bb.Left * imgWidth;
    const cellTop = bb.Top * imgHeight;
    const cellWidth = bb.Width * imgWidth;
    const cellHeight = bb.Height * imgHeight;
  
    // Get the center point of the cell
    const cellCenterX = cellLeft + (cellWidth / 2);
    const cellCenterY = cellTop + (cellHeight / 2);
  
    // Get the container dimensions (using fixed height from container style)
    const containerHeight = 250; // matches the height in containerStyle
    const containerWidth = containerRef.current?.clientWidth || 500; // fallback if ref not available
  
    // Calculate a padding factor that ensures enough context around the cell
    // Using a smaller factor (2.0) to zoom in more as requested
    const paddingFactor = 2.0;
    
    // Calculate snippet dimensions to include padding
    const desiredSnippetWidth = cellWidth * paddingFactor;
    const desiredSnippetHeight = cellHeight * paddingFactor;
    
    // Calculate scale factor to fit the snippet within the container
    const scaleX = containerWidth / desiredSnippetWidth;
    const scaleY = containerHeight / desiredSnippetHeight;
    const scale = Math.min(scaleX, scaleY, 1.0); // Never scale up beyond 1.0
    
    // Calculate the actual dimensions of the snippet area after scaling
    const snippetWidth = desiredSnippetWidth * scale;
    const snippetHeight = desiredSnippetHeight * scale;
    
    // Calculate snippet position to center the cell in the viewport
    // Make sure to account for the full container height for proper vertical centering
    const snippetLeft = Math.max(0, cellCenterX - (snippetWidth / 2));
    const snippetTop = Math.max(0, cellCenterY - (snippetHeight / 2));
    
    // Adjust if we're too close to the right or bottom edge
    const adjustedSnippetLeft = Math.min(snippetLeft, imgWidth - snippetWidth);
    const adjustedSnippetTop = Math.min(snippetTop, imgHeight - snippetHeight);
    
    // Calculate the position of the image within the container to center the cell
    const imageLeft = -adjustedSnippetLeft;
    const imageTop = -adjustedSnippetTop;
    
    // Calculate highlight position relative to the image position
    const highlightLeft = cellLeft + imageLeft;
    const highlightTop = cellTop + imageTop;
  
    console.log("Cell position in image:", {
      imgDimensions: `${imgWidth}x${imgHeight}`,
      cellInPixels: `(${cellLeft.toFixed(0)},${cellTop.toFixed(0)}) ${cellWidth.toFixed(0)}x${cellHeight.toFixed(0)}`,
      snippetArea: `(${adjustedSnippetLeft.toFixed(0)},${adjustedSnippetTop.toFixed(0)}) ${snippetWidth.toFixed(0)}x${snippetHeight.toFixed(0)}`,
      cellInViewport: `(${highlightLeft.toFixed(0)},${highlightTop.toFixed(0)})`,
      scale: scale.toFixed(2),
      containerDimensions: `${containerWidth}x${containerHeight}`
    });
  
    // The container style - this is the viewport into the image
    const containerStyle = {
      width: '100%',
      height: '250px',
      overflow: 'hidden',
      position: 'relative',
      display: 'flex',  // Added flex display
      alignItems: 'center',  // Center vertically
      justifyContent: 'center',  // Center horizontally
    };
    
    // Create a wrapper div to help with centering
    const wrapperStyle = {
      position: 'relative',
      width: Math.min(imgWidth * scale, containerWidth),
      height: Math.min(imgHeight * scale, containerHeight),
    };
    
    // The image style - positioned to show the cell centered
    const imageStyle = {
      position: 'absolute',
      width: imgWidth,
      height: imgHeight,
      left: imageLeft,
      top: imageTop,
      maxWidth: 'none',
      transformOrigin: 'top left',
    };
    
    // The highlight box style - positioned relative to the image
    const highlightStyle = {
      position: 'absolute',
      left: highlightLeft,
      top: highlightTop,
      width: cellWidth,
      height: cellHeight,
      border: '2px solid rgba(255, 0, 0, 0.8)',
      backgroundColor: 'rgba(255, 0, 0, 0.2)',
      pointerEvents: 'none'
    };
    
    return (
      <div ref={containerRef} style={containerStyle}>
        <div style={wrapperStyle}>
          <img 
            ref={imageRef}
            src={imageUrl}
            alt="Document with highlighted cell"
            style={imageStyle}
            onError={() => setImageError(true)}
          />
          <div style={highlightStyle}></div>
        </div>
        
        {/* Debug info */}

        {/*
        <div className="absolute top-2 left-2 text-xs bg-black/80 text-white p-1 rounded z-10">
          Cell: [{cellData.row},{cellData.col}] • {cellWidth.toFixed(0)}x{cellHeight.toFixed(0)}px
        </div>
        */}
      </div>
    );
  };

  return (
    <div 
      className={`flex flex-col w-full max-w-lg mx-auto
        ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}
        transform transition-all duration-300
        ${isAnimating === 'right' ? 'translate-x-full opacity-0' : 
          isAnimating === 'left' ? '-translate-x-full opacity-0' : ''}
      `}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {/* Document snippet preview */}
      <div className={`rounded-lg overflow-hidden border-2 mb-4 relative
        ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-300 bg-white'}`}>
        {snippet ? (
          // PDF snippet display - data URL approach
          <div className="relative" style={{ minHeight: '120px' }}>
            <img 
              src={snippet} 
              alt="Document snippet" 
              className="max-w-full h-auto object-contain"
            />
            <div className={`absolute inset-0 pointer-events-none border-2 border-red-500 bg-red-500/10`} />
          </div>
        ) : imageError ? (
          // Error state
          <div className={`flex items-center justify-center h-32
            ${isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>
            Failed to load image snippet
          </div>
        ) : renderDirectPositionedImage() || (
          // Fallback when no snippet can be rendered
          <div className={`flex items-center justify-center h-32
            ${isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>
            {imageUrl ? 'Loading image dimensions...' : 'No preview available'}
          </div>
        )}
      </div>

      {/* Cell content editor */}
      <div className="mb-4">
        <textarea
          value={content}
          onChange={handleInputChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none
            ${isDarkMode 
              ? 'bg-gray-800 border-gray-700 focus:ring-blue-600' 
              : 'bg-white border-gray-300 focus:ring-blue-500'}`}
          rows={2}
          placeholder="Enter correct text here..."
          autoFocus
        />
      </div>

      {/* Info and coordinates */}
      <div className={`text-xs mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {cellData ? (
          <div className="flex flex-wrap gap-2">
            <span>Row: {cellData.row + 1}</span>
            <span>Column: {String.fromCharCode(65 + cellData.col)}</span>
            {cellData.confidence && <span>Confidence: {cellData.confidence.toFixed(1)}%</span>}
            {hasBoundingBox() && (
              <span className="font-mono">
                Pos: [{cellData.boundingBox.Left.toFixed(2)}, {cellData.boundingBox.Top.toFixed(2)}] 
                Size: [{cellData.boundingBox.Width.toFixed(2)}, {cellData.boundingBox.Height.toFixed(2)}]
              </span>
            )}
          </div>
        ) : (
          <span>Cell information not available</span>
        )}
      </div>

      {/* Action buttons */}
        <div className="mt-6 flex justify-between gap-4">
          {/* Previous button - only enabled when there's history */}
          <button
            onClick={onPrevious}
            disabled={!canGoBack}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg
              ${isDarkMode 
                ? canGoBack 
                  ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : canGoBack
                  ? 'bg-indigo-500 hover:bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
          >
            <ArrowUp size={16} />
            <span>Previous</span>
            <span className="text-xs opacity-70">(↑)</span>
          </button>

          {/* Skip button */}
          <button
            onClick={handleSkip}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg
              ${isDarkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
          >
            <ArrowLeft size={16} />
            <span>Skip</span>
            <span className="text-xs opacity-70">(←)</span>
          </button>

          {/* Approve button */}
          <button
            onClick={() => handleApprove(content)} 
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg 
              ${isDarkMode 
                ? 'bg-green-600 hover:bg-green-500 text-white' 
                : 'bg-green-500 hover:bg-green-600 text-white'}`}
          >
            <span>Approve</span>
            {isEdited ? <Check size={16} /> : <ArrowRight size={16} />}
            <span className="text-xs opacity-70">(→)</span>
          </button>
        </div>
    </div>
  );
};

export default QuickDrawCard;